import type { Track, User, UserSubscription } from '@shady-pines/db';
import type { Uuid } from '@shady-pines/types';
import { now, strongFromEntries } from '@shady-pines/types';
import { isNil } from 'lodash';
import type { StoreApi } from 'zustand';
import { supabaseApi } from '../api/api-store';
import type { TrackState } from './types';

export type TrackSubscriptionsApi = ReturnType<typeof TrackSubscriptionsApi>;

export const TrackSubscriptionsApi = (
  get: StoreApi<TrackState>['getState'],
  set: StoreApi<TrackState>['setState'],
) =>
  ({
    fetchTrackSubscriptions: async (userId: Uuid<User> | null) => {
      const loading = get().loading.subscriptions;

      if (loading) {
        return;
      }

      if (isNil(userId)) {
        set(
          (s) =>
            ({
              trackSubscriptionsById: {},
              loading: {
                ...s.loading,
                subscriptions: false,
              },
            }) as const satisfies Partial<TrackState>,
        );
        return;
      }

      const { data: subscriptions } = await supabaseApi()
        .from('user_subscriptions')
        .select('*')
        .is('deleted_at', null)
        .eq('subscription_type', 'track')
        .eq('user_id', userId);

      const strongSubscriptions = (subscriptions ?? []) as UserSubscription<'track'>[];

      set(
        (s) =>
          ({
            trackSubscriptionsById: strongFromEntries(
              strongSubscriptions.map((i) => [i.track_id, true]),
            ),
            loading: {
              ...s.loading,
              subscriptions: false,
            },
          }) as const satisfies Partial<TrackState>,
      );
    },
    updateTrackSubscription: async (userId: Uuid<User>, id: Uuid<Track>) => {
      const loading = get().loading.updateSubscriptionsById[id];

      if (loading) {
        return;
      }

      set(
        (s) =>
          ({
            loading: {
              ...s.loading,
              updateSubscriptionsById: {
                ...s.loading,
                [id]: true,
              },
            },
          }) as const satisfies Partial<TrackState>,
      );

      const isSubscribed = get().trackSubscriptionsById?.[id];

      const { error } = await (async () => {
        if (isSubscribed) {
          const { error: subscribeError } = await supabaseApi()
            .from('user_subscriptions')
            .update({
              updated_at: now(),
              deleted_at: now(),
            })
            .eq('user_id', userId)
            .eq('subscription_type', 'track')
            .eq('track_id', id);

          return { error: subscribeError };
        }
        const { error: subscribeError } = await supabaseApi().from('user_subscriptions').insert({
          track_id: id,
          user_id: userId,
          subscription_type: 'track',
        });

        return { error: subscribeError };
      })();

      if (isNil(error)) {
        set(
          (s) =>
            ({
              trackSubscriptionsById: {
                ...s.trackSubscriptionsById,
                [id]: !isSubscribed,
              },
            }) as const satisfies Partial<TrackState>,
        );
      }

      set(
        (s) =>
          ({
            loading: {
              ...s.loading,
              updateSubscriptionsById: {
                ...s.loading,
                [id]: false,
              },
            },
          }) as const satisfies Partial<TrackState>,
      );

      return error;
    },
  }) as const;
